import React, { useEffect, useMemo } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions';

import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import type {
	PageRovoConfigProviderProps,
	RovoConfiguration,
	RovoConfigurationProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';

import { useNudgeRecommendationFilter } from './hooks/useNudgeRecommendationFilter.tsx';

const ViewPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const { contentType, contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;

	const viewPageRovoActions = useViewPageRovoActions();
	const enableNudgeCheck = useNudgeRecommendationFilter(contentType, contentId);
	const { setNudges } = useAISmartButtonActions();

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);
				return Promise.resolve(actions);
			},
		};
	}, [viewPageRovoActions]);

	useEffect(() => {
		setConfiguration(config);

		if (enableNudgeCheck) {
			const pageCatchupAction = viewPageRovoActions.find(
				(action: { actionId: string }) => action.actionId === 'page-catchup',
			)?.action;
			if (pageCatchupAction) {
				const allNudges: ConversationAction[] = [pageCatchupAction];
				setNudges(allNudges);
			}
		} else {
			setNudges([]);
		}
	}, [config, setConfiguration, enableNudgeCheck, viewPageRovoActions, setNudges]);

	return null;
};

export const ViewPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps();
	return (
		<CommentCountsProvider contentId={contentId}>
			<ViewPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
